import React, { useState, useEffect, useCallback } from 'react';
import { Form, Button, Container, Row, Col, Table, Card, InputGroup } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { getClienti, searchItems, createPreventivo } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PreventivoPagamentoFields from '../../common/PreventivoPagamentoFields';
import { calcolaTotali, lordoUnitario, nettoDaLordo } from '../../common/preventivoUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';

const NuovoPreventivo = () => {
  // State for dropdowns and suggestions
  const [allClienti, setAllClienti] = useState([]); // All clients for dropdown
  const [searchResults, setSearchResults] = useState([]); // Unified search results for services

  // Main preventivo state
  const [preventivo, setPreventivo] = useState({
    cliente_id: null,
    cliente_nome: '',
    cliente_email: '',
    cliente_telefono: '',
    data_scadenza: '', // Optional
    note: '',
    dettagli: [],
    subtotale: 0,
    iva_totale: 0,
    totale: 0,
    condizioni_pagamento: [],
    acconto_percentuale: '',
    acconto_importo: '',
    banca_id: '',
    note_cliente: '',
    includi_link_accettazione: true,
  });

  // State for the new line item form
  const [selectedService, setSelectedService] = useState(null); // Selected service from typeahead
  const [quantita, setQuantita] = useState(1);
  const [isCustomServiceMode, setIsCustomServiceMode] = useState(false); // New state for custom service mode
  const [customServiceDescription, setCustomServiceDescription] = useState('');
  const [customServicePrice, setCustomServicePrice] = useState(0);
  const [customServiceCost, setCustomServiceCost] = useState(0);
  const [customServiceIva, setCustomServiceIva] = useState(22);
  const [prezziIvati, setPrezziIvati] = useState(true); // true: i prezzi digitati sono IVA inclusa

  const navigate = useNavigate();

  // Fetch initial data (clients)
  useEffect(() => {
    const fetchData = async () => {
      try {
        const clientiRes = await getClienti(0, 100000);
        if (clientiRes && clientiRes.data && Array.isArray(clientiRes.data.clienti)) {
          setAllClienti(clientiRes.data.clienti);
        } else {
          console.error("Errore: la risposta dell'API per i clienti non è nel formato previsto.", clientiRes);
          setAllClienti([]);
        }
      } catch (error) {
        console.error("Errore nel caricamento dei dati iniziali", error);
        toast.error("Errore nel caricamento dei dati iniziali.");
      }
    };
    fetchData();
  }, []);

  const handlePreventivoChange = useCallback((e) => {
    const { name, value } = e.target;
    setPreventivo(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleClienteChange = useCallback((selected) => {
    if (selected.length > 0) {
      const cliente = selected[0];
      setPreventivo(prev => ({ 
        ...prev, 
        cliente_id: cliente.id,
        cliente_nome: `${cliente.nome || ''} ${cliente.cognome || cliente.denominazione_sociale || ''}`.trim(),
        cliente_email: cliente.email,
        cliente_telefono: cliente.telefono || cliente.cellulare,
      }));
    } else {
      setPreventivo(prev => ({ ...prev, cliente_id: null, cliente_nome: '', cliente_email: '', cliente_telefono: '' }));
    }
  }, []);

  const handleSearchServices = useCallback(async (searchText) => {
    if (searchText.length > 2) {
      try {
        const response = await searchItems({ query: searchText, item_type: "Servizio" });
        setSearchResults(response.data.filter(item => item.item_type === "Servizio"));
      } catch (error) {
        console.error("Errore durante la ricerca dei servizi:", error);
        setSearchResults([]);
      }
    } else {
      setSearchResults([]);
    }
  }, []);

  const addLineItem = () => {
    if (isCustomServiceMode) {
        if (!customServiceDescription || quantita <= 0 || customServicePrice <= 0) {
            toast.error("Inserisci descrizione, quantità e prezzo validi per il servizio personalizzato.");
            return;
        }
        const item = {
            prodotto_libero: true, // Questo campo non esiste nel modello DettaglioPreventivo, è solo per coerenza frontend
            descrizione: customServiceDescription,
            prezzo_unitario: parseFloat(customServicePrice),
            costo_unitario: parseFloat(customServiceCost),
            iva_percentuale: parseFloat(customServiceIva),
            quantita: parseInt(quantita),
            // Campi aggiuntivi per visualizzazione tabella se necessario, non vanno al backend
            titolo_display: customServiceDescription,
        };
        setPreventivo(prev => ({ ...prev, dettagli: [...prev.dettagli, item] }));
        setCustomServiceDescription('');
        setCustomServicePrice(0);
        setCustomServiceCost(0);
        setCustomServiceIva(22);
        setQuantita(1);
    } else {
        if (!selectedService) {
            toast.error("Seleziona un servizio.");
            return;
        }
        if (quantita <= 0) {
            toast.error("La quantità deve essere maggiore di zero.");
            return;
        }
        
        const itemToAdd = {
            servizio_id: selectedService.id,
            descrizione: selectedService.titolo, // Usiamo il titolo del servizio come descrizione
            quantita: parseInt(quantita),
            prezzo_unitario: prezziIvati
              ? (selectedService.prezzo || selectedService.prezzo_indicativo_da || 0)
              : nettoDaLordo(selectedService.prezzo || selectedService.prezzo_indicativo_da || 0, selectedService.iva || 22),
            iva_percentuale: selectedService.iva || 22,
            costo_unitario: selectedService.costo || 0,
            opzioni_selezionate_json: selectedService.opzioni_configurabili_json, // Mantieniamo le opzioni dal servizio
            titolo_display: selectedService.titolo, // Per visualizzazione frontend
        };

        setPreventivo(prev => ({ ...prev, dettagli: [...prev.dettagli, itemToAdd] }));
        setSelectedService(null);
        setQuantita(1);
    }
  };

  const cambiaModalitaPrezzi = (ivati) => {
    setPrezziIvati(ivati);
    setPreventivo(prev => ({
      ...prev,
      dettagli: prev.dettagli.map(d => ({
        ...d,
        prezzo_unitario: ivati
          ? lordoUnitario(d.prezzo_unitario, d.iva_percentuale, false)
          : nettoDaLordo(d.prezzo_unitario, d.iva_percentuale),
      })),
    }));
  };

  const aggiornaCampiPagamento = useCallback((patch) => {
    setPreventivo(prev => ({ ...prev, ...patch }));
  }, []);

  const removeLineItem = (index) => {
    setPreventivo(prev => ({ ...prev, dettagli: prev.dettagli.filter((_, i) => i !== index) }));
  };

  const handlePriceChange = (e, index) => {
    const newPrice = parseFloat(e.target.value) || 0;
    const updatedDettagli = preventivo.dettagli.map((item, i) => {
        if (i === index) {
            return { ...item, prezzo_unitario: newPrice };
        }
        return item;
    });
    setPreventivo(prev => ({ ...prev, dettagli: updatedDettagli }));
  };

  const calculateTotals = useCallback(() => {
    const totali = calcolaTotali(preventivo.dettagli, prezziIvati);
    setPreventivo(prev => ({ ...prev, ...totali }));
  }, [preventivo.dettagli, prezziIvati]);

  useEffect(() => {
    calculateTotals();
  }, [calculateTotals]);

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!preventivo.cliente_email && !preventivo.cliente_id) {
        toast.error("Seleziona un cliente esistente o inserisci almeno l'email per un cliente guest.");
        return;
    }
    if (preventivo.dettagli.length === 0) {
        toast.error("Aggiungi almeno un servizio al preventivo.");
        return;
    }

    const condizioni = preventivo.condizioni_pagamento || [];
    if (condizioni.includes('ACCONTO') && !(parseFloat(preventivo.acconto_importo) > 0 || parseFloat(preventivo.acconto_percentuale) > 0)) {
        toast.error("Hai scelto l'acconto: indica una percentuale o un importo.");
        return;
    }
    if (condizioni.includes('BONIFICO') && !preventivo.banca_id) {
        toast.error('Hai scelto il bonifico: seleziona la banca.');
        return;
    }

    try {
      const preventivoToSubmit = {
          cliente_id: preventivo.cliente_id,
          cliente_nome: preventivo.cliente_nome,
          cliente_email: preventivo.cliente_email,
          cliente_telefono: preventivo.cliente_telefono,
          data_scadenza: preventivo.data_scadenza || null,
          note: preventivo.note,
          condizioni_pagamento: preventivo.condizioni_pagamento || [],
          acconto_percentuale: (preventivo.condizioni_pagamento || []).includes('ACCONTO') && parseFloat(preventivo.acconto_percentuale) > 0 ? parseFloat(preventivo.acconto_percentuale) : null,
          acconto_importo: (preventivo.condizioni_pagamento || []).includes('ACCONTO') && parseFloat(preventivo.acconto_importo) > 0 ? parseFloat(preventivo.acconto_importo) : null,
          banca_id: (preventivo.condizioni_pagamento || []).includes('BONIFICO') && preventivo.banca_id ? parseInt(preventivo.banca_id, 10) : null,
          note_cliente: preventivo.note_cliente || null,
          includi_link_accettazione: preventivo.includi_link_accettazione !== false,
          dettagli: preventivo.dettagli.map(d => ({
              servizio_id: d.servizio_id,
              descrizione: d.descrizione,
              quantita: d.quantita,
              prezzo_unitario: lordoUnitario(d.prezzo_unitario, d.iva_percentuale, prezziIvati),
              iva_percentuale: d.iva_percentuale,
              costo_unitario: d.costo_unitario,
              opzioni_selezionate_json: d.opzioni_selezionate_json,
          }))
      };
      await createPreventivo(preventivoToSubmit);
      toast.success('Preventivo creato con successo!');
      navigate('/preventivi');
    } catch (error) {
      console.error('Errore nella creazione del preventivo:', error.response?.data || error);
      toast.error(`Errore nella creazione del preventivo: ${error.response?.data?.detail || error.message}`);
    }
  };

  return (
    <Container>
        <Row className="align-items-center mb-3">
            <Col>
                <h2>Nuovo Preventivo</h2>
            </Col>
            <Col xs="auto" className="ms-auto">
                <Button variant="secondary" onClick={() => navigate('/preventivi')} className="me-2">
                    Torna alla Lista
                </Button>
                <Button variant="primary" type="submit" form="preventivo-form">
                    <FontAwesomeIcon icon={faPlus} className="me-2" /> Salva Preventivo
                </Button>
            </Col>
        </Row>
      <Form id="preventivo-form" onSubmit={handleSubmit}>
        <Card className="mb-4">
          <Card.Header>Dati Cliente e Preventivo</Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Form.Group as={Col} md={6} controlId="formCliente">
                <Form.Label>Cliente Esistente</Form.Label>
                <InputGroup>
                  <Typeahead
                    id="cliente-typeahead"
                    labelKey={option => {
                      const namePart = [option.nome, option.cognome].filter(Boolean).join(' ');
                      const companyPart = option.denominazione_sociale ? ` (${option.denominazione_sociale})` : '';
                      return (namePart + companyPart).trim() || 'Cliente Sconosciuto';
                    }}
                    onChange={handleClienteChange}
                    options={allClienti}
                    placeholder="Cerca o seleziona un cliente..."
                    selected={preventivo.cliente_id ? allClienti.filter(c => c.id === preventivo.cliente_id) : []}
                    filterBy={['nome', 'cognome', 'denominazione_sociale', 'email']}
                    disabled={!!(preventivo.cliente_nome || preventivo.cliente_email || preventivo.cliente_telefono)} // Disabilita se ci sono dati guest
                  />
                  <Button variant="outline-secondary" onClick={() => navigate('/clienti/new', { state: { fromPreventivo: true } })}>
                    Nuovo Cliente
                  </Button>
                </InputGroup>
                <Form.Text className="text-muted">
                    Seleziona un cliente esistente oppure compila i campi sottostanti per un cliente "guest".
                </Form.Text>
              </Form.Group>
              <Form.Group as={Col} md={6} controlId="formDataScadenza">
                <Form.Label>Data Scadenza Preventivo (Opzionale)</Form.Label>
                <Form.Control 
                  type="date" 
                  name="data_scadenza" 
                  value={preventivo.data_scadenza} 
                  onChange={handlePreventivoChange} 
                />
              </Form.Group>
            </Row>
            
            <hr className="my-4"/>

            <Row className="mb-3">
              <Col>
                <h5>Dati Cliente "Guest" (se non selezionato un cliente esistente)</h5>
              </Col>
            </Row>
            <Row className="mb-3">
                <Form.Group as={Col} md={4} controlId="formClienteNome">
                    <Form.Label>Nome Cliente</Form.Label>
                    <Form.Control
                        type="text"
                        name="cliente_nome"
                        value={preventivo.cliente_nome}
                        onChange={handlePreventivoChange}
                        placeholder="Nome Cognome o Denominazione"
                        disabled={!!preventivo.cliente_id} // Disabilita se è selezionato un cliente esistente
                    />
                </Form.Group>
                <Form.Group as={Col} md={4} controlId="formClienteEmail">
                    <Form.Label>Email Cliente</Form.Label>
                    <Form.Control
                        type="email"
                        name="cliente_email"
                        value={preventivo.cliente_email}
                        onChange={handlePreventivoChange}
                        placeholder="email@example.com"
                        disabled={!!preventivo.cliente_id}
                    />
                </Form.Group>
                <Form.Group as={Col} md={4} controlId="formClienteTelefono">
                    <Form.Label>Telefono Cliente</Form.Label>
                    <Form.Control
                        type="text"
                        name="cliente_telefono"
                        value={preventivo.cliente_telefono}
                        onChange={handlePreventivoChange}
                        placeholder="Telefono"
                        disabled={!!preventivo.cliente_id}
                    />
                </Form.Group>
            </Row>
            <Row className="mb-3">
                <Form.Group as={Col} md={12} controlId="formNote">
                    <Form.Label>Note (Opzionale)</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        name="note"
                        value={preventivo.note}
                        onChange={handlePreventivoChange}
                        placeholder="Note interne sul preventivo..."
                    />
                </Form.Group>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <span>Dettagli Servizi/Prodotti</span>
            <Form.Check
              type="switch"
              id="prezzi-ivati"
              label="Prezzi IVA inclusa"
              checked={prezziIvati}
              onChange={(e) => cambiaModalitaPrezzi(e.target.checked)}
            />
          </Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Check
                    inline
                    type="radio"
                    label="Servizio Esistente"
                    name="serviceType"
                    id="serviceTypeExisting"
                    checked={!isCustomServiceMode}
                    onChange={() => setIsCustomServiceMode(false)}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    label="Servizio Personalizzato"
                    name="serviceType"
                    id="serviceTypeCustom"
                    checked={isCustomServiceMode}
                    onChange={() => setIsCustomServiceMode(true)}
                  />
                </Form.Group>
              </Col>
            </Row>

            {!isCustomServiceMode ? (
              <Row className="align-items-end mb-3">
                <Form.Group as={Col} md={9} controlId="formServiceSearch">
                  <Form.Label>Aggiungi Servizio</Form.Label>
                  <Typeahead
                    id="service-search-typeahead"
                    labelKey={(option) => `${option.titolo} (SKU: ${option.sku || 'N/A'})`}
                    options={searchResults}
                    onInputChange={handleSearchServices}
                    onChange={(selected) => {
                      if (selected.length > 0) {
                        setSelectedService(selected[0]);
                      } else {
                        setSelectedService(null);
                      }
                    }}
                    placeholder="Cerca o seleziona un servizio..."
                    selected={selectedService ? [selectedService] : []}
                    filterBy={() => true}
                  />
                  {selectedService && <Form.Text className="text-muted">
                    Prezzo indicativo: €{selectedService.prezzo_indicativo_da ? selectedService.prezzo_indicativo_da.toFixed(2) : '0.00'}
                  </Form.Text>}
                </Form.Group>
                <Form.Group as={Col} md={2} controlId="formQuantita">
                  <Form.Label>Quantità</Form.Label>
                  <Form.Control 
                    type="number" 
                    value={quantita} 
                    onChange={(e) => setQuantita(parseInt(e.target.value))} 
                    min="1" 
                  />
                </Form.Group>
                <Col md={1} className="d-flex align-items-end">
                  <Button variant="secondary" onClick={addLineItem} className="w-100">
                    <FontAwesomeIcon icon={faPlus} />
                  </Button>
                </Col>
              </Row>
            ) : (
              <Row className="align-items-end mb-3">
                <Form.Group as={Col} md={4} controlId="formCustomServiceDescription">
                  <Form.Label>Descrizione Servizio</Form.Label>
                  <Form.Control
                    type="text"
                    value={customServiceDescription}
                    onChange={(e) => setCustomServiceDescription(e.target.value)}
                    placeholder="Descrizione servizio personalizzato"
                  />
                </Form.Group>
                <Form.Group as={Col} md={2} controlId="formCustomServicePrice">
                  <Form.Label>Prezzo Unitario {prezziIvati ? '(IVA incl.)' : '(netto)'}</Form.Label>
                  <Form.Control
                    type="number"
                    value={customServicePrice}
                    onChange={(e) => setCustomServicePrice(parseFloat(e.target.value))}
                    step="0.01"
                    min="0"
                  />
                </Form.Group>
                <Form.Group as={Col} md={2} controlId="formCustomServiceCost">
                  <Form.Label>Costo Unitario</Form.Label>
                  <Form.Control
                    type="number"
                    value={customServiceCost}
                    onChange={(e) => setCustomServiceCost(parseFloat(e.target.value))}
                    step="0.01"
                    min="0"
                  />
                </Form.Group>
                <Form.Group as={Col} md={2} controlId="formCustomServiceIva">
                  <Form.Label>IVA %</Form.Label>
                  <Form.Control
                    type="number"
                    value={customServiceIva}
                    onChange={(e) => setCustomServiceIva(parseFloat(e.target.value))}
                    step="0.01"
                    min="0"
                  />
                </Form.Group>
                <Form.Group as={Col} md={1} controlId="formQuantitaCustom">
                  <Form.Label>Quantità</Form.Label>
                  <Form.Control
                    type="number"
                    value={quantita}
                    onChange={(e) => setQuantita(parseInt(e.target.value))}
                    min="1"
                  />
                </Form.Group>
                <Col md={1} className="d-flex align-items-end">
                  <Button variant="secondary" onClick={addLineItem} className="w-100">
                    <FontAwesomeIcon icon={faPlus} />
                  </Button>
                </Col>
              </Row>
            )}

            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Servizio</th>
                  <th>Qtà</th>
                  <th>Prezzo Unit. {prezziIvati ? '(IVA incl.)' : '(netto)'}</th>
                  <th>IVA %</th>
                  <th>Totale riga (IVA incl.)</th>
                  <th>Costo Unit.</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                  {preventivo.dettagli.map((item, index) => {
                      const prezzo_effettivo = item.prezzo_unitario;
                      const sub = lordoUnitario(prezzo_effettivo, item.iva_percentuale, prezziIvati) * item.quantita;
                      return (
                        <tr key={index}>
                          <td>{item.descrizione || (item.titolo_display || 'N/A')}</td>
                          <td>{item.quantita}</td>
                          <td>
                            <Form.Control
                                type="number"
                                value={prezzo_effettivo}
                                onChange={(e) => handlePriceChange(e, index)}
                                step="0.01"
                                style={{ minWidth: '100px' }}
                            />
                          </td>
                          <td>{item.iva_percentuale}%</td>
                          <td>€ {sub.toFixed(2)}</td>
                          <td>€ {item.costo_unitario ? item.costo_unitario.toFixed(2) : '0.00'}</td>
                          <td>
                            <Button variant="danger" size="sm" onClick={() => removeLineItem(index)}>
                                <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          </td>
                        </tr>
                      );
                  })}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        <PreventivoPagamentoFields value={preventivo} onChange={aggiornaCampiPagamento} />

        <Card className="mb-4">
          <Card.Header>Riepilogo Preventivo</Card.Header>
          <Card.Body>
            <Row>
                <Col md={{ span: 4, offset: 8 }}>
                    <p className="d-flex justify-content-between"><strong>Subtotale Imponibile:</strong> <span>€ {preventivo.subtotale.toFixed(2)}</span></p>
                    <p className="d-flex justify-content-between"><strong>IVA Totale:</strong> <span>€ {preventivo.iva_totale.toFixed(2)}</span></p>
                    <hr />
                    <h4 className="d-flex justify-content-between"><strong>Totale Preventivo (IVA inclusa):</strong> <span>€ {preventivo.totale.toFixed(2)}</span></h4>
                </Col>
            </Row>
          </Card.Body>
        </Card>
      </Form>
    </Container>
  );
};

export default NuovoPreventivo;
